import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { useState } from 'react';
import { Col, Container, Row, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import TulipIcon from '../../../assets/tulip.svg';
import Button from '../../../components/Button';
import Label from '../../../components/Label';
import Blockquote from '../../../components/Blockquote';
import DefaultCard from '../../../components/DefaultCard';
import CustomSelect from '../../../components/Select/Select';
import { HEALTH_INSURANCE_ROUTE } from '../../../routes/types';
import {
  addOrRemoveDaysToGivenDate,
  addOrRemoveMonthsToGivenDate,
  DateFormats,
  formatDate,
  openExternalLink,
} from '../../../utils/utils';

const today = new Date();

const currentEventsData = [
  {
    eventName: 'Birth/Adoption',
    deadline: `Deadline: ${formatDate(addOrRemoveDaysToGivenDate(today, 30))}`,
    status: 'Completed',
    link: `${HEALTH_INSURANCE_ROUTE}`,
  },

  {
    eventName: 'Pension Benefit Commencement',
    deadline: `Initial Request Date: ${formatDate(
      addOrRemoveDaysToGivenDate(today, 25)
    )}`,
    status: 'In Process',
    link: '/pension/retire-online/required',
  },
];

const lifeEventOptions = [
  { value: '1', label: 'Birth/Adoption' },
  { value: '2', label: 'Child Loses Eligibility' },
  { value: '3', label: 'Child Status Update' },
  { value: '4', label: 'Daycare Cost Change' },
  { value: '5', label: 'Divorce/Legal Separation' },
  { value: '6', label: 'Employee/Partner Gain Coverage' },
  { value: '7', label: 'Employee/Partner Lose Coverage' },
  { value: '8', label: 'Insurance/Post Tax Change' },
  { value: '9', label: 'Marriage/Partnership' },
];

export type LifeEventChoiceProps = React.PropsWithChildren<{
  onClick?: React.MouseEventHandler;
  selectValue: string | number;
  onChange?: (selectValue: any) => void;
}>;

const LifeEventChoice = ({
  onClick,
  selectValue,
  onChange,
}: LifeEventChoiceProps) => {
  const navigate = useNavigate();
  const today = new Date();

  const handleRedirect = (link: string) => {
    return navigate(link);
  };

  return (
    <Container className={'cnd-custom-container py-11'}>
      <Container className={'px-0'}>
        <Row className={'mb-12'}>
          <Col className={'col-12 col-lg-6 py-0 py-lg-9'}>
            <h2 className={'text-sm-24-lg-48 mb-7'}>Start a New Life Event</h2>
            <span className={'text-fs-6 fw-light'}>
              Select a life event you’d like to preview or report.
            </span>
            <Row className={'d-none d-lg-block mt-12'}>
              <Col className={'col-12'}>
                <h2 className={'text-sm-20-lg-24'}>
                  Preview or Report a Life Event
                </h2>
              </Col>
              <Col className={'col-12 mt-10 mb-11'}>
                <CustomSelect
                  className="live-event"
                  options={lifeEventOptions}
                  onChange={onChange}
                  placeholder={'Select a Life Event'}
                  value={selectValue}
                />
                {selectValue === '1' && (
                <Card className="custom-generic-card bg-blue mt-10">
                  <Card.Body>
                    <Card.Text className="bg-blue">
                      <Label
                        leftIcon={light('circle-exclamation')}
                     
                      >
                      <span className={'text-indigo-darker'}><strong>Birth/Adoption</strong></span>
                      </Label>
                      <p className={'text-center text-lg-start  text-sub1 text-indigo-darker fw-light lh-xs'}>Choose this event to add your new child to coverage. You can add any of your other children that don't currently have coverage, as long as they are under age 26. Click <strong>Continue</strong> to proceed.</p>
                    </Card.Text>
                  </Card.Body>
                </Card>
                )}
              </Col>

             

              <Col className={'col-4'}>
                <Button
                  disabled={selectValue !== '1'}
                  className={'col-12'}
                  onClick={onClick}
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </Col>
          <Col className={'col-12 col-lg-6 text-center my-12 m-lg-0'}>
            <img
              src={`${process.env.PUBLIC_URL}/${'life-events.png'}`}
              alt="woman-holding-baby"
            />
          </Col>
        </Row>

        <Row className={'d-block d-lg-none mb-12'}>
          <Col className={'col-12'}>
            <h2 className={'text-sm-20-lg-24'}>
              Preview or Report a Life Event
            </h2>
          </Col>
          <Col className={'col-12 mt-10 mb-11'}>
            <CustomSelect
              options={lifeEventOptions}
              onChange={onChange}
              placeholder={'Select a Life Event'}
              value={selectValue}
            />
          </Col>
          <Col>
            <Button className={'col-12'} onClick={onClick}>
              Continue
            </Button>
          </Col>
        </Row>
      </Container>
      <Row>
        <Col className={'col-12 col-lg-7 mb-7 mb-lg-0'}>
          <DefaultCard
            isCurrentEvent
            className={'h-100'}
            leftHeading="Current Events"
            leftSubheading={
              <Container className={'px-0'}>
                <Container className={'px-0 d-none d-lg-block'}>
                  <Row className={'py-3 mx-0 border-bottom-light-gray-2px'}>
                    <Col className={'col-3 ps-0'}>
                      <span className={'text-fs-6 fw-normal'}>Plan</span>
                    </Col>

                    <Col className={'col-3 ps-0'}>
                      <span className={'text-fs-6 fw-normal'}>Deadline</span>
                    </Col>

                    <Col className={'col-3'}>
                      <span className={'text-fs-6 fw-normal'}>Status</span>
                    </Col>

                    <Col className={'col-3 pe-0'}></Col>
                  </Row>
                  {currentEventsData.map((currentEventData, idx) => (
                    <Row
                      key={idx}
                      className={`align-items-center py-7 mx-0 ${
                        idx !== currentEventsData.length - 1
                          ? 'border-bottom-light-gray-lg'
                          : ''
                      }`}
                    >
                      <Col className={'col-3 ps-0'}>
                        <span className={'text-sm-12-lg-14 fw-normal'}>
                          {currentEventData.eventName}
                        </span>
                      </Col>

                      <Col className={'col-3 text-caption ps-0'}>
                        <span className={'fw-light'}>
                          {currentEventData.deadline}
                        </span>
                      </Col>

                      <Col className={'col-3 text-caption'}>
                        <span className={'fw-light'}>
                          {currentEventData.status}
                        </span>
                      </Col>

                      <Col className={'col-3 text-end pe-0'}>
                        <Button
                          size={'sm'}
                          variant={'primary-shadow'}
                          onClick={() => handleRedirect(currentEventData.link)}
                        >
                          Edit
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </Container>

                <Container className={'px-0 d-block d-lg-none'}>
                  {currentEventsData.map((currentEventData, idx) => (
                    <Row className={'mx-0'} key={idx}>
                      <Col
                        className={'col-12 px-0 border-bottom-light-gray-lg'}
                      >
                        <Row className={`align-items-end py-3`}>
                          <Col className={'col-8'}>
                            <Row>
                              <Col className={'col-12'}>
                                <span
                                  className={
                                    'text-sm-12-lg-14 text-nowrap fw-normal'
                                  }
                                >
                                  {currentEventData.eventName}
                                </span>
                              </Col>

                              <Col className={'col-12'}>
                                <span
                                  className={
                                    'text-caption text-nowrap fw-light'
                                  }
                                >{`Deadline: ${currentEventData.deadline}`}</span>
                              </Col>

                              <Col className={'col-12'}>
                                <span
                                  className={
                                    'text-caption text-nowrap fw-light'
                                  }
                                >{`Status: ${currentEventData.status}`}</span>
                              </Col>
                            </Row>
                          </Col>

                          <Col className={'col-4 text-end'}>
                            <Button size={'sm'} variant={'primary-shadow'}>
                              Edit
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  ))}
                </Container>
              </Container>
            }
            variant={'blue'}
          />
        </Col>

        <Col className={'col-12 col-lg-5'}>
          <Blockquote lineVariant="default warning">
            <h5>Important</h5>
              <ul>
                <li className="text-ligther-grey pl-10">
                If your life event occured in a prior pay period, you could incur retroactive premium deductions back to that date.
                </li>
              </ul>
                
              </Blockquote>
        </Col>

      </Row>
    </Container>
  );
};

export default LifeEventChoice;
